import { Component, Vue } from 'nuxt-property-decorator'
import type { AxiosError } from 'axios'
import { Debounce } from '~/utils/decorators/debounce'
import { $api } from '~/plugins/axios'
import { addressObservable } from '~/observables/modules/address'
import { OnBeforeMount } from '~/core/vue.types'
import { CitiesResponse, HouseResponse, StreetResponse } from '~/core/offer.types'
import { omitNonDigitPipe } from '~/utils/common.utils'

export enum AddressTypes {
  ZIP = 'zip',
  CITY = 'city',
  STREET = 'street',
  HOUSE = 'house',
}

@Component
export default class AddressMixin extends Vue implements OnBeforeMount {
  protected AddressTypes = Object.freeze(AddressTypes)

  protected citiesOptions: CitiesResponse['cities'] = []
  protected streetsOptions: StreetResponse['streets'] = []
  protected houseNumbersOptions: HouseResponse['houses'] = []

  protected isAddressAvailable = true

  protected addressObservable = addressObservable

  protected isAllowFreeCityInput = false

  get isZipValid (): boolean {
    return !!this.addressObservable.zip && this.addressObservable.zip.length === 4
  }

  get isCityValid (): boolean {
    return !!this.addressObservable.city
  }

  get isStreetValid (): boolean {
    return !!this.addressObservable.street
  }

  get isNumberValid (): boolean {
    return !!this.addressObservable.number
  }

  get isAddressValid () {
    return this.isZipValid && this.isCityValid && this.isStreetValid && this.isNumberValid
  }

  async onInputSearch (event: Event | string = '', type: AddressTypes) {
    const value = typeof event === 'string'
      ? event
      : (event.target as HTMLInputElement).value

    switch (type) {
      case AddressTypes.CITY:
        if (this.isAllowFreeCityInput) {
          this.setCitiesOptions([value])
        }
        break

      case AddressTypes.STREET:
        await this.fetchStreets(value)
        break

      case AddressTypes.HOUSE:
        await this.fetchHouseNumbers(value)
        break
    }
  }

  async onInputChange (event: Event | string, type: AddressTypes) {
    const value = typeof event === 'string'
      ? event
      : (event.target as HTMLInputElement).value

    switch (type) {
      case AddressTypes.ZIP:
        // Omit non-digit symbols and make sure the replaced value doesn't equal
        // to the value in observable to prevent additional API-request to happen
        // Usually the equal values case occurs if passing non-digit symbols
        // in the field using copy/paste
        if (this.addressObservable.zip === omitNonDigitPipe(value)) {
          return
        }

        this.addressObservable.zip = omitNonDigitPipe(value)

        this.resetCity()
        this.resetStreet()
        this.resetNumber()

        // Do not trigger fetching until ZIP characters length less than 4
        if (this.addressObservable.zip.length < 4) {
          return
        }

        await this.fetchData(AddressTypes.CITY)
        break

      case AddressTypes.CITY:
        this.addressObservable.city = value
        this.$emit('city-changed', value)

        this.resetStreet()
        this.resetNumber()
        await this.fetchData(AddressTypes.STREET)
        break

      case AddressTypes.STREET:
        this.addressObservable.street = value
        this.$emit('street-changed', value)

        this.resetNumber()
        await this.fetchData(AddressTypes.HOUSE)
        break

      case AddressTypes.HOUSE:
        this.addressObservable.number = value
        this.$emit('number-changed', value)
        break
    }
  }

  @Debounce(500)
  async fetchData (type: AddressTypes) {
    switch (type) {
      case AddressTypes.CITY:
        await this.fetchCities()
        break
      case AddressTypes.STREET:
        await this.fetchStreets()
        break
      case AddressTypes.HOUSE:
        await this.fetchHouseNumbers()
        break
    }
  }

  resetZip () {
    this.addressObservable.zip = ''
  }

  resetCity () {
    this.setCitiesOptions()
    this.addressObservable.city = ''
  }

  resetStreet () {
    this.setStreetsOptions()
    this.addressObservable.street = ''
  }

  resetNumber () {
    this.setHouseNumbersOptions()
    this.addressObservable.number = ''
  }

  resetAddressField () {
    this.resetZip()
    this.resetCity()
    this.resetStreet()
    this.resetNumber()

    this.isAddressAvailable = true
  }

  async fetchCities () {
    this.resetStreet()
    this.resetNumber()

    try {
      let response = null

      response = await $api.PublicAddressService.citiesAutocomplete({
        zip: this.addressObservable.zip,
      })

      this.setCitiesOptions(response.cities)

      this.isAllowFreeCityInput = false

      // Prefill with city to avoid additional clicks to select the city
      if (response.cities.length === 1) {
        this.addressObservable.city = response.cities[0]
      }
    } catch (e) {
      const { response } = e as AxiosError

      if (response) {
        if (response.status === 404 || response.status === 400) {
          return
        }

        if (response.status === 422) {
          return
        }

        if (response.status >= 500) {
          this.isAllowFreeCityInput = true
          return
        }
      }

      throw e
    }
  }

  async fetchStreets (search = '') {
    if (!search || search.length < 2) {
      return
    }

    this.resetNumber()

    try {
      let response = null

      response = await $api.PublicAddressService.streetsAutocomplete({
        zip: this.addressObservable.zip,
        street: search,
      })

      this.setStreetsOptions(response.streets)
    } catch (e) {
      console.info(e)
    }
  }

  async fetchHouseNumbers (search = '') {
    if (!search || search.length < 1) {
      return
    }

    let response = null

    try {
      response = await $api.PublicAddressService.housesAutocomplete({
        zip: this.addressObservable.zip,
        street: this.addressObservable.street,
        house: search,
      })

      this.setHouseNumbersOptions(response.houses)
    } catch (e) {
      console.info(e)
    }
  }

  setCitiesOptions (options: CitiesResponse['cities'] = []) {
    this.citiesOptions = options
  }

  setStreetsOptions (options: StreetResponse['streets'] = []) {
    this.streetsOptions = options
  }

  setHouseNumbersOptions (options: HouseResponse['houses'] = []) {
    this.houseNumbersOptions = [
      this.$t('fields.none').toString(),
      ...options,
    ]
  }

  async beforeMount () {
    if (this.isZipValid && !this.isCityValid) {
      await this.fetchCities()
    }
  }
}
